import { Directive, ElementRef, Input, OnInit, Renderer2, inject } from '@angular/core';

@Directive({
  selector: '[payLibSkeletonLoader]',
  standalone: true,
})
export class SkeletonLoaderDirective implements OnInit {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);

  private _skeletonCondition!: boolean;

  @Input() set skeletonCondition(value: boolean) {
    if (value === this._skeletonCondition) {
      return;
    }

    // We don't have a skeleton loader, and we are not going to remove it
    if (!this._skeletonCondition && !value) {
      return;
    }

    this._skeletonCondition = value;

    if (this._skeletonCondition) {
      this.showSkeletonLoader();
    } else {
      this.hideSkeletonLoader();
    }
  }

  // Height is required to ensure the skeleton loader actually appears
  @Input() heightClass: string = 'min-h-40';
  @Input() widthClass?: string = 'w-full';
  @Input() bgColor?: string = 'bg-neutral';
  @Input() hasRounding?: boolean = true;
  @Input() rounding?: string = 'rounded-2xl';
  @Input() customClasses?: string[] = [];

  ngOnInit(): void {
    // Image elements should be completed once the image has loaded
    const tagName = this.el.nativeElement.tagName.toLowerCase();
    if (tagName === 'img') {
      // Set the loader to true from the start
      this.skeletonCondition = true;

      const imgElement = this.el.nativeElement as HTMLImageElement;
      imgElement.onerror = () => (this.skeletonCondition = false);
      imgElement.onload = () => (this.skeletonCondition = false);
    }
  }

  showSkeletonLoader(): void {
    const skeletonLoader = this.renderer.createElement('div');

    this.renderer.addClass(skeletonLoader, 'skeleton');
    this.renderer.addClass(skeletonLoader, 'border');
    this.renderer.addClass(skeletonLoader, 'border-neutral-8');
    this.renderer.addClass(skeletonLoader, 'overflow-y-hidden');

    this.bgColor && this.renderer.addClass(skeletonLoader, `!${this.bgColor}`);
    this.hasRounding && this.rounding && this.renderer.addClass(skeletonLoader, this.rounding);

    this.heightClass && this.renderer.addClass(skeletonLoader, this.heightClass);
    this.widthClass && this.renderer.addClass(skeletonLoader, this.widthClass);

    this.customClasses?.forEach((customClass) => {
      this.renderer.addClass(skeletonLoader, customClass);
    });

    const el = this.el.nativeElement;
    const parent = el.parentNode;

    this.renderer.insertBefore(parent, skeletonLoader, el);

    // Here we place el in skeleton loader
    this.renderer.appendChild(skeletonLoader, el);

    // Hide base element and its children
    this.renderer.addClass(el, 'hidden');
  }

  hideSkeletonLoader() {
    const el = this.el.nativeElement;

    // Node to be removed
    const existingParentContainerDiv = el.parentNode;

    // We want this to become the parent of el
    const existingParentContainerDivParent = existingParentContainerDiv.parentNode;

    // Reinsert the element after the parent
    this.renderer.insertBefore(existingParentContainerDivParent, el, existingParentContainerDiv);

    // Remove the temp container
    this.renderer.removeChild(existingParentContainerDivParent, existingParentContainerDiv);

    this.renderer.removeClass(el, 'hidden');
  }
}
